import type { SelectOption } from "Components/ui";
import type { AuditLogEntry } from "api/auditLogs";

/** Action-type filter; each maps to the `actionContains` tokens the old table sent. */
export type ActionTypeFilter = "all" | "create" | "update" | "delete";

/**
 * Resource filter, sent as `actionPrefix` (actions are named `<RESOURCE>_<VERB>`). The backend
 * applies only one of `action`, `actionPrefix` and `actionContains`, so a resource and an action
 * type can't be combined (routes/auditLogs.js buildAuditLogFilter).
 */
export type ResourceFilter =
  | "all"
  | "TENANT"
  | "BILLING"
  | "BILLING_PAYMENT"
  | "USER"
  | "ROLE"
  | "MODULE"
  | "NOTIFICATION_TEMPLATE"
  | "SYSTEM_CONFIG";

/** Preset windows ending now, or a custom calendar range. */
export type RangeFilter = "7" | "30" | "90" | "custom";

export interface AuditLogFilterState {
  /** Raw search box text; the request uses a debounced copy. */
  search: string;
  actionType: ActionTypeFilter;
  resource: ResourceFilter;
  /** "all", "system" (entries no admin made) or an actor's ObjectId. */
  actor: string;
  range: RangeFilter;
  /** "YYYY-MM-DD" bounds for the custom range (local calendar days). */
  from: string;
  to: string;
}

/** Tone family of an action, for its badge. */
export type ActionKind =
  | "create"
  | "update"
  | "delete"
  | "payment"
  | "sensitive"
  | "auth"
  | "other";

export interface AuditLogsData {
  logs: AuditLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  setPage: (page: number) => void;
  loading: boolean;
  /** The last load failure, or null. */
  error: unknown;
  dismissError: () => void;
  reload: () => void;
  exporting: boolean;
  exportCsv: () => Promise<void>;
  filters: AuditLogFilterState;
  setFilter: <K extends keyof AuditLogFilterState>(key: K, value: AuditLogFilterState[K]) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  /** Actors seen in the entries loaded so far; there is no actor list endpoint for this role. */
  actorOptions: SelectOption[];
}

export const ACTION_TYPE_OPTIONS: SelectOption<ActionTypeFilter>[] = [
  { value: "all", label: "All actions" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
];

export const RESOURCE_OPTIONS: SelectOption<ResourceFilter>[] = [
  { value: "all", label: "All resources" },
  { value: "TENANT", label: "Tenants" },
  { value: "BILLING", label: "Billing" },
  { value: "BILLING_PAYMENT", label: "Payments" },
  { value: "USER", label: "Users" },
  { value: "ROLE", label: "Roles" },
  { value: "MODULE", label: "Modules" },
  { value: "NOTIFICATION_TEMPLATE", label: "Notification templates" },
  { value: "SYSTEM_CONFIG", label: "System settings" },
];

export const RANGE_OPTIONS: SelectOption<RangeFilter>[] = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "custom", label: "Custom range" },
];

export const DEFAULT_FILTERS: AuditLogFilterState = {
  search: "",
  actionType: "all",
  resource: "all",
  actor: "all",
  range: "30",
  from: "",
  to: "",
};
